import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ImagePlus, Send, WandSparkles, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { name: "", phone: "", product_interest: "", quantity: "", location: "", message: "" };

export default function Enquiry() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [photo, setPhoto] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number");
      return;
    }
    setSending(true);
    try {
      let attachment = "";
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        attachment = (await axios.post(`${API}/enquiries/attachment`, fd)).data.url;
      }
      const res = await axios.post(`${API}/enquiries`, { ...form, attachment });
      toast.success(res.data.message || "Enquiry sent! We will reach out shortly.");
      setForm(EMPTY);
      setPhoto(null);
    } catch {
      toast.error("Could not send your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="enquiry" className="bg-cream py-20 sm:py-28" data-testid="enquiry-section">
      <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 sm:px-8 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.22em] text-terracotta">Bulk & Custom Orders</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-ragi-dark" data-testid="enquiry-title">
            Wholesale millets, wedding boxes & custom treats
          </h2>
          <p className="mt-4 max-w-lg text-sm sm:text-base leading-relaxed text-muted-foreground">
            Need millets at wholesale prices, gift hampers for a celebration, or a
            custom batch of laddus and chocolates? Send us an enquiry and we will call
            you back with pricing and delivery options.
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {[
              ["Wholesale pricing", "on all 6 millet varieties"],
              ["Custom gifting", "3D chocolates & fusion laddo boxes"],
              ["Made to order", "fresh batches after confirmation"],
              ["Doorstep delivery", "location shared after enquiry"],
            ].map(([title, sub]) => (
              <div key={title} className="rounded-2xl border border-cream-muted bg-cream-secondary p-5">
                <p className="font-display text-lg font-semibold text-ragi-dark">{title}</p>
                <p className="mt-1 text-xs text-muted-foreground">{sub}</p>
              </div>
            ))}
          </div>
          <Link to="/image-studio" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-terracotta transition-colors hover:text-ragi" data-testid="enquiry-image-studio-link"><WandSparkles className="h-4 w-4" />Create a custom food concept</Link>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-3xl border border-cream-muted bg-cream-secondary p-6 sm:p-8 shadow-lg shadow-ragi/5"
          data-testid="enquiry-form"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-ragi-dark" htmlFor="enq-name">Full Name *</label>
              <Input id="enq-name" data-testid="enquiry-name-input" value={form.name} onChange={set("name")} placeholder="Your name" className="bg-cream" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-ragi-dark" htmlFor="enq-phone">Phone / WhatsApp *</label>
              <Input id="enq-phone" data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} placeholder="98XXXXXXXX" className="bg-cream" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-ragi-dark" htmlFor="enq-product">Products of Interest</label>
              <Input id="enq-product" data-testid="enquiry-product-input" value={form.product_interest} onChange={set("product_interest")} placeholder="e.g. Protein laddu, millets" className="bg-cream" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-ragi-dark" htmlFor="enq-qty">Quantity Needed</label>
              <Input id="enq-qty" data-testid="enquiry-quantity-input" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 5 kg / 50 boxes" className="bg-cream" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-xs font-semibold text-ragi-dark" htmlFor="enq-loc">Delivery Location</label>
              <Input id="enq-loc" data-testid="enquiry-location-input" value={form.location} onChange={set("location")} placeholder="City / Area" className="bg-cream" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-xs font-semibold text-ragi-dark" htmlFor="enq-msg">Customizations / Message</label>
              <Textarea id="enq-msg" data-testid="enquiry-message-input" value={form.message} onChange={set("message")} placeholder="Flavours, sugar-free options, gifting notes..." rows={4} className="bg-cream" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-xs font-semibold text-ragi-dark" htmlFor="enq-photo">Reference photo (optional)</label>
              <input id="enq-photo" type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => { setPhoto(e.target.files?.[0] || null); e.target.value = ""; }} data-testid="enquiry-photo-input" />
              {photo ? (
                <div className="flex items-center gap-3" data-testid="enquiry-photo-preview">
                  <img src={URL.createObjectURL(photo)} alt="Reference" className="h-14 w-14 rounded-xl object-cover" />
                  <span className="truncate text-xs text-muted-foreground">{photo.name}</span>
                  <button type="button" onClick={() => setPhoto(null)} className="rounded-full p-1 text-muted-foreground hover:text-terracotta" aria-label="Remove photo" data-testid="enquiry-photo-remove"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <label htmlFor="enq-photo" className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-dashed border-ragi/30 bg-cream px-4 py-2 text-xs font-semibold text-ragi-dark hover:border-terracotta" data-testid="enquiry-photo-button"><ImagePlus className="h-4 w-4" /> Attach a gift box / cake idea</label>
              )}
            </div>
          </div>
          <p className="mt-4 text-xs leading-relaxed text-muted-foreground" data-testid="enquiry-analysis-notice">Enquiry text may be automatically categorised to help our kitchen respond.</p>
          <button
            type="submit"
            disabled={sending}
            data-testid="enquiry-submit-button"
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-terracotta px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-orange-900/20 transition-all hover:bg-terracotta-dark disabled:opacity-60"
          >
            <Send className="h-4 w-4" />
            {sending ? "Sending..." : "Send Enquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
